import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAsync } from "../lib/useAsync";
import { fetchActivity } from "../api/worker";

const ACTION_LABELS: Record<string, string> = {
  question_created: "Created question",
  question_updated: "Updated question",
  question_deleted: "Deleted question",
  question_bulk_uploaded: "Bulk uploaded questions",
  question_bulk_deleted: "Bulk deleted questions",
  topic_created: "Created topic",
  subtopic_created: "Created subtopic",
  topic_updated: "Updated topic",
  subtopic_updated: "Updated subtopic",
  pqx_book_uploaded: "Uploaded book",
  pqx_batch_imported: "Book imported",
  pqx_question_approved: "Published book questions",
};

const ACTION_COLORS: Record<string, string> = {
  question_created: "bg-positive/15 text-positive",
  question_updated: "bg-accent/15 text-accent-light",
  question_deleted: "bg-negative/15 text-negative",
  question_bulk_uploaded: "bg-positive/15 text-positive",
  question_bulk_deleted: "bg-negative/15 text-negative",
  topic_created: "bg-positive/15 text-positive",
  subtopic_created: "bg-positive/15 text-positive",
  pqx_book_uploaded: "bg-accent/15 text-accent-light",
  pqx_batch_imported: "bg-positive/15 text-positive",
  pqx_question_approved: "bg-positive/15 text-positive",
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ActivityScreen() {
  const [page, setPage] = useState(1);
  const { data, loading } = useAsync(() => fetchActivity(page, 30), [page]);

  return (
    <div>
      <h1 className="text-[18px] font-bold lg:text-[22px]">Activity Log</h1>
      <p className="mt-1 text-[13px] text-text-muted">
        Every change made in the worker dashboard is recorded here.
      </p>

      {loading ? (
        <p className="mt-8 text-text-muted">Loading…</p>
      ) : (
        <div className="mt-6 flex flex-col gap-2">
          {data?.entries.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-col gap-2 rounded-lg border border-border bg-surface px-3 py-3 sm:flex-row sm:items-start sm:gap-4 sm:px-4"
            >
              <span
                className={`mt-0.5 shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${
                  ACTION_COLORS[entry.action] ?? "bg-surface-high text-text-muted"
                }`}
              >
                {ACTION_LABELS[entry.action] ?? entry.action}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px]">
                  <span className="font-semibold">
                    {entry.worker.firstName ?? entry.worker.email}
                  </span>
                  {entry.details && "textExcerpt" in entry.details ? (
                    <span className="text-text-muted">
                      {" "}
                      — "{(entry.details as { textExcerpt: string }).textExcerpt}"
                    </span>
                  ) : null}
                  {entry.details && "count" in entry.details ? (
                    <span className="text-text-muted">
                      {" "}
                      — {(entry.details as { count: number }).count} items
                    </span>
                  ) : null}
                  {entry.details && "name" in entry.details ? (
                    <span className="text-text-muted">
                      {" "}
                      — {(entry.details as { name: string }).name}
                    </span>
                  ) : null}
                </p>
                <p className="mt-0.5 text-[11px] text-text-muted">{formatTime(entry.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && data.total > 30 ? (
        <div className="mt-5 flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded px-3 py-1 text-[13px] bg-surface text-text-muted hover:bg-surface-high disabled:opacity-40"
          >
            <span className="inline-flex items-center gap-1">
              <ChevronLeft size={14} aria-hidden="true" /> Prev
            </span>
          </button>
          <span className="text-[13px] text-text-muted">Page {page}</span>
          <button
            type="button"
            disabled={(data?.entries.length ?? 0) < 30}
            onClick={() => setPage((p) => p + 1)}
            className="rounded px-3 py-1 text-[13px] bg-surface text-text-muted hover:bg-surface-high disabled:opacity-40"
          >
            <span className="inline-flex items-center gap-1">
              Next <ChevronRight size={14} aria-hidden="true" />
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
